import type { AssetPack } from '~/../types/AssetPack';
import { App, app } from '../App';
import { Log } from '../utils/Log';

export interface SpineAsset {
  key: string;
  jsonURL?: string;
  atlasURL?: string | string[];
  preMultipliedAlpha?: boolean;
  textureXhrSettings?: Phaser.Types.Loader.XHRSettingsObject;
  atlasXhrSettings?: Phaser.Types.Loader.XHRSettingsObject;
}

export interface SceneAssets {
  hasDynamicAssets?: boolean;
  requiredAssetPacks?: string[];
  requiredSpineAssets?: string[];
  requiredAudio?: string[];
}

export interface AssetsToLoad {
  assetPacks: string[];
  spines: string[];
  audio: string[];
}

export interface SpineRawAtlas {
  data: string;
  preMultipliedAlpha: boolean;
  prefix: string;
}


export class AssetManager {
  protected areFontsLoaded: boolean;

  protected assetPackLookup: Map<string, any> | undefined;
  protected spineAssetLookup: Map<string, SpineAsset> | undefined;
  protected audioAssetLookup: Set<string> | undefined;

  protected baseAssets: Set<string> | undefined;
  protected sceneIdToAssetDependencies: Map<string, SceneAssets> | undefined;

  protected loadedAssetPacks: Set<string>;
  protected loadedSpineAssets: Set<string>;
  protected loadedAudioAssets: Set<string>;
  protected dynamicAssetPacks: Set<string>;
  protected dynamicSpineAssets: Set<string>;
  protected dynamicAudioAssets: Set<string>;

  constructor() {
    this.areFontsLoaded = false;

    this.loadedAssetPacks = new Set();
    this.loadedSpineAssets = new Set();
    this.loadedAudioAssets = new Set();
    this.dynamicAssetPacks = new Set();
    this.dynamicSpineAssets = new Set();
    this.dynamicAudioAssets = new Set();
  }

  get AreFontsLoaded() {
    return this.areFontsLoaded;
  }

  public fontsReady() {
    this.areFontsLoaded = true;
    app.emit(App.events.FontsLoaded);
  }

  public setAssetMaps(
    assetPackLookup: Map<string, any>,
    spineAssetLookup: Map<string, SpineAsset>,
    audioAssetLookup: Set<string>,
    baseAssets: Set<string>,
    sceneIdToAssets: Map<string, SceneAssets>
  ) {
    this.assetPackLookup = assetPackLookup;
    this.spineAssetLookup = spineAssetLookup;
    this.audioAssetLookup = audioAssetLookup;

    this.baseAssets = baseAssets;
    this.sceneIdToAssetDependencies = sceneIdToAssets;
  }

  public addDynamicAssetPacks(assetPacks: string[]) {
    for (const assetPackId of assetPacks) {
      this.addDynamicAssetPack(assetPackId);
    }
  }

  public addDynamicAssetPack(assetPackId: string) {
    this.dynamicAssetPacks.add(assetPackId);
  }

  public addDynamicAssets(dynamicAssets: AssetsToLoad) {
    for (const assetPackId of dynamicAssets.assetPacks) {
      this.addDynamicAssetPack(assetPackId);
    }

    for (const spineAsset of dynamicAssets.spines) {
      this.dynamicSpineAssets.add(spineAsset);
    }

    for (const audioAssets of dynamicAssets.audio) {
      this.dynamicAudioAssets.add(audioAssets);
    }
  }

  public resetPostLoad() {
    this.dynamicAssetPacks.clear();
    this.dynamicSpineAssets.clear();
    this.dynamicAudioAssets.clear();
  }

  public loadAssetsForScene(scene: Phaser.Scene) {
    let sceneAssets = this.sceneIdToAssetDependencies?.get(scene.scene.key);
    if (!sceneAssets || this.assetPackLookup == null || this.spineAssetLookup == null || this.audioAssetLookup == null || this.baseAssets == null) return;

    // TODO: Merge base assets and required assets.

    if (sceneAssets.requiredAssetPacks) {
      for (const assetPackId of sceneAssets.requiredAssetPacks) {
        let asset = this.assetPackLookup.get(assetPackId);
        if (asset) {
          scene.load.pack(assetPackId, asset);
          this.loadedAssetPacks.add(assetPackId);
          Log.badge(Log.badgeColours[0], 'Loading Asset', `${assetPackId}`);
          // Log.sectionBadge(Log.Section.LoadingAsset, `${assetPackId}`);
        }
      }
    }

    if (sceneAssets.requiredSpineAssets) {
      for (const spineId of sceneAssets.requiredSpineAssets) {
        let asset = this.spineAssetLookup?.get(spineId);
        if (asset && asset.jsonURL && asset.atlasURL) {
          scene.load.spine(asset.key, asset.jsonURL, asset.atlasURL, asset.preMultipliedAlpha, asset.textureXhrSettings, asset.atlasXhrSettings);
          this.loadedSpineAssets.add(spineId);
          Log.badge(Log.badgeColours[0], 'Loading Asset', `${spineId}`);
        }
      }
    }

    if (sceneAssets.hasDynamicAssets) {
      for (const assetPackId of this.dynamicAssetPacks) {
        let asset = this.assetPackLookup.get(assetPackId);
        if (asset) {
          scene.load.pack(assetPackId, asset);
          this.loadedAssetPacks.add(assetPackId);
          Log.badge(Log.badgeColours[0], 'Loading Dynamic Asset', `${assetPackId}`);
        }
      }

      for (const spineId of this.dynamicSpineAssets) {
        let asset = this.spineAssetLookup?.get(spineId);
        if (asset && asset.jsonURL && asset.atlasURL) {
          scene.load.spine(asset.key, asset.jsonURL, asset.atlasURL, asset.preMultipliedAlpha, asset.textureXhrSettings, asset.atlasXhrSettings);
          this.loadedSpineAssets.add(spineId);
          Log.badge(Log.badgeColours[0], 'Loading Dynamic Asset', `${spineId}`);
        }
      }
    }
  }

  public unloadAssets(scene: Phaser.Scene) {
    let sceneAssets = this.sceneIdToAssetDependencies?.get(scene.scene.key);
    if (!sceneAssets || this.assetPackLookup == null || this.spineAssetLookup == null || this.audioAssetLookup == null || this.baseAssets == null) return;

    let loadedAssetPacksToCheck = new Set(this.loadedAssetPacks);
    let loadedSpinesToCheck = new Set(this.loadedSpineAssets);
    let loadedAudioToCheck = new Set(this.loadedAudioAssets);

    Log.badge(Log.badgeColours[0], 'Base Assets Loaded', `${new Array(...this.baseAssets).join(', ')}`);
    Log.badge(Log.badgeColours[0], 'Asset Packs Loaded', `${new Array(...loadedAssetPacksToCheck).join(', ')}`);
    Log.badge(Log.badgeColours[0], 'Spines Loaded', `${new Array(...loadedSpinesToCheck).join(', ')}`);
    Log.badge(Log.badgeColours[0], 'Audio Loaded', `${new Array(...loadedAudioToCheck).join(', ')}`);

    for (const assetPackId of loadedAssetPacksToCheck) {
      // If the asset is not in this scene's required assets, or dynamic assets, or in the base assets, then we can unload the asset.
      if (!sceneAssets.requiredAssetPacks?.includes(assetPackId) && !this.dynamicAssetPacks.has(assetPackId) && !this.baseAssets.has(assetPackId)) {
        this.unloadPack(scene, assetPackId);
        this.loadedAssetPacks.delete(assetPackId);
        Log.badge(Log.badgeColours[0], 'Unloading Asset', `${assetPackId}`);
      }
    }

    for (const spineId of loadedSpinesToCheck) {
      // If the asset is not in this scene's required assets, or dynamic assets, or in the base assets, then we can unload the asset.
      if (!sceneAssets.requiredSpineAssets?.includes(spineId) && !this.dynamicSpineAssets.has(spineId) && !this.baseAssets.has(spineId)) {
        this.unloadSpine(scene, spineId);
        this.loadedSpineAssets.delete(spineId);
        Log.badge(Log.badgeColours[0], 'Unloading Asset', `${spineId}`);
      }
    }
  }

  protected unloadPack(scene: Phaser.Scene, packId: string) {
    let assetPack = scene.cache.json.get(packId);
    if (!assetPack) return;

    for (const key of Object.keys(assetPack)) {
      if (key == 'meta') continue;

      if (Array.isArray(assetPack[key]?.files)) {
        for (const file of assetPack[key].files) {
          if (file.type == 'multiatlas') {
            scene.textures.remove(file.key);

            // let animations = animData.filter((val) => {
            //   console.log(val);
            //   return val.key.indexOf(file.key + '-') == 0;
            // });

            // for (let i = 0; i < animations.length; i++) {
            //   scene.anims.remove(animations[i].key);
            // }
          }
        }
      }
    }

    scene.cache.json.remove(packId);
  }

  protected unloadSpine(scene: Phaser.Scene, spineId: string) {
    let json = scene.cache.json.get(spineId);
    let rawAtlas: SpineRawAtlas = scene.cache.custom.spine.get(spineId);
    let processedAtlas: spine.TextureAtlas = scene.cache.custom.spineTextures.get(spineId);

    /**
     * If an instance of a spine object has been created then we can use the processed atlas to find the textures.
     * If no instance has been created then we need to process the atlas text to find the textures. Luckily there's
     * always a blank line before each texture file name.
     */
    if (processedAtlas) {
      // Remove all textures.
      for (const page of processedAtlas.pages) {
        scene.textures.remove(page.name);
      }

      // Clear out raw atlas.
      scene.cache.custom.spine.remove(spineId);
      // Clear out processed atlas.
      scene.cache.custom.spineTextures.remove(spineId);
      // Remove skeleton json.
      scene.cache.json.remove(spineId);
    } else if (rawAtlas) {
      //  Inspect the atlas data to find the texture files.
      var content = rawAtlas.data.split('\n');
      //  Extract and remove the textures.
      var textures = [];
      for (var t = 0; t < content.length; t++) {
        var line = content[t];
        if (line.trim() === '' && t < content.length - 1) {
          line = content[t + 1];
          textures.push(line);
          scene.textures.remove(line);
        }
      }

      // Clear out raw atlas.
      scene.cache.custom.spine.remove(spineId);
      // Clear out processed atlas.
      scene.cache.custom.spineTextures.remove(spineId);
      // Remove skeleton json.
      scene.cache.json.remove(spineId);
    }
  }
}
